import { UserProfileRole, UserStatus } from '@@types/api.types';
import { CompanyProfileModal } from '@components/pages/company/components/company-company-profile-modal.component.tsx';
import { CompanyProfilePendingInvitesModal } from '@components/pages/company/components/company-company-profile-pending-invites.component';
import { ExpertProfileModal } from '@components/pages/expert-profile/components/expert-profile-modal.component';
import { UserProfileModal } from '@components/pages/home/components/home-user-profile-modal.component.tsx';
import { TermsAndConditionsModal } from '@components/pages/home/components/home-user-terms-and-conditions-modal.component.tsx';
import { TechnologyTechnologyModal } from '@components/pages/technology/components/technology-technology-modal.component.tsx';
import { useGetCurrentUser } from '@queries/users.query';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

type ModalType =
  | 'companyPendingInvites'
  | 'createCompany'
  | 'createExpertProfile'
  | 'createTechnology'
  | 'createUserProfile'
  | 'termsAndConditions';

export const AutomaticModalsComponent: React.FC = () => {
  const [activeModal, setActiveModal] = useState<ModalType>();
  const [companyModalSeen, setCompanyModalSeen] = useState(false);
  const { data: user } = useGetCurrentUser();
  const location = useLocation();

  const closeAllModals = useCallback(() => setActiveModal(undefined), []);

  const openModal = useCallback(
    (modalType: ModalType) => {
      closeAllModals();
      setActiveModal(modalType);
    },
    [closeAllModals],
  );

  const isInnovator = user?.profile?.role === UserProfileRole.INNOVATOR;
  const isExpert = user?.profile?.role === UserProfileRole.EXPERT;
  const userHasValidAccess = Boolean(user?.hasValidAccess);
  const hasCompany = user?.companies && user.companies.length > 0;

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want this useEffect to run on every location change
  useEffect(() => {
    if (!user) return;

    if (!user.emailVerified || user.status === UserStatus.BLOCKED) {
      return;
    }

    if (!user.profile) {
      openModal('createUserProfile');
      return;
    }

    if (!user.profile.hasAcceptedTerms) {
      openModal('termsAndConditions');
      return;
    }

    if (isExpert && !user.expertProfile) {
      openModal('createExpertProfile');
      return;
    }

    if (user.invites && user.invites.length > 0) {
      openModal('companyPendingInvites');
      return;
    }

    if (!userHasValidAccess) return;

    if (isInnovator && (!user.companies || (user.companies.length === 0 && !companyModalSeen))) {
      openModal('createCompany');
      setCompanyModalSeen(true);
      return;
    }
  }, [companyModalSeen, hasCompany, isInnovator, isExpert, location, openModal, user, userHasValidAccess]);

  const onContinueCompanyModal = () => {
    openModal('createTechnology');
  };

  const onContinueUserProfileModal = () => {
    if (isExpert) {
      openModal('createExpertProfile');
    } else {
      closeAllModals();
    }
  };

  const onInviteDecision = () => {
    // If there is another invite, keep the modal open
    //
    if (!user?.invites || user.invites.length <= 1) {
      closeAllModals();
    }
  };

  return (
    <>
      {activeModal === 'createUserProfile' && (
        <UserProfileModal
          isOpen
          onClose={closeAllModals}
          onContinue={onContinueUserProfileModal}
        />
      )}

      {activeModal === 'termsAndConditions' && (
        <TermsAndConditionsModal
          isOpen
          onClose={closeAllModals}
        />
      )}

      {activeModal === 'companyPendingInvites' && (
        <CompanyProfilePendingInvitesModal
          isOpen
          onClose={closeAllModals}
          invites={user?.invites || []}
          onInviteAccepted={onInviteDecision}
          onInviteRejected={onInviteDecision}
        />
      )}

      {activeModal === 'createExpertProfile' && (
        <ExpertProfileModal
          isOpen
          onClose={closeAllModals}
        />
      )}

      {activeModal === 'createCompany' && (
        <CompanyProfileModal
          isOpen
          onClose={closeAllModals}
          onContinue={onContinueCompanyModal}
          onCompanyCreated={() => {}}
        />
      )}

      {activeModal === 'createTechnology' && (
        <TechnologyTechnologyModal
          isEdit={false}
          isOpen
          onClose={closeAllModals}
        />
      )}
    </>
  );
};
